// Пустое состояние (components/EmptyState/README.md): иконка → заголовок → одна фраза →
// не больше одного действия. «Ещё нет» — кнопка-акцент, «ничего не найдено» — вторичная
// «Сбросить фильтры», «всё сделано» — без кнопки. Ошибка — с кодом, без технических подробностей;
// с номером операции — строкой ниже «Код … · операция …» и «Скопировать номер» (FR-028).
import { CircleAlert, Inbox } from 'lucide-react';
import type { ReactNode } from 'react';
import { cx } from '../lib/cx.ts';
import { Icon } from '../lib/icon.tsx';
import type { LucideIcon } from '../lib/icon.tsx';
import { OperationCode } from './OperationCode.tsx';

export interface EmptyStateProps {
  /**
   * Заголовок. Без него — тело страницы под заголовком page-title (отказ, 404, ошибка внутри
   * каркаса): значок, код и действия.
   */
  title?: ReactNode;
  /** Одна фраза: что здесь появится и как. */
  text?: ReactNode;
  icon?: LucideIcon;
  /** Действие; у ошибки — «На главную» и «Повторить». */
  action?: ReactNode;
  /** Ошибка: иконка color-bad. */
  error?: boolean;
  /** Код ошибки моноширинным шрифтом: «Код ошибки SYS-001». */
  code?: string;
  /** Номер операции из ответа сервера: «Код SYS-001 · операция 7F3K-92QD-1A2B». */
  operation?: string;
  /** 1 — экран целиком (404, ошибка), 2 — список на странице. */
  headingLevel?: 1 | 2 | 3 | 4;
  className?: string;
}

export function EmptyState({
  title,
  text,
  icon,
  action,
  error = false,
  code,
  operation,
  headingLevel = 2,
  className,
}: EmptyStateProps) {
  const Heading = `h${headingLevel}` as const;
  return (
    <div className={cx('t-empty', error && 'err', className)} role={error ? 'alert' : undefined}>
      <div className="glyph" aria-hidden="true">
        <Icon icon={icon ?? (error ? CircleAlert : Inbox)} />
      </div>
      {title != null ? <Heading className="kp-empty-title">{title}</Heading> : null}
      {text != null || code != null ? (
        <p>
          {text}
          {code != null && operation ? (
            <OperationCode code={code} operation={operation} className="kp-opline" />
          ) : code != null ? (
            <>
              {text != null ? ' ' : null}
              Код ошибки <span className="kp-code">{code}</span>
            </>
          ) : null}
        </p>
      ) : null}
      {action != null ? <div className="t-row">{action}</div> : null}
    </div>
  );
}
